import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  User,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  BarChart3,
  Upload,
  RefreshCw,
  Sparkles,
  ChevronRight,
  Brain,
  MessageSquare,
  FileDown,
  LogOut,
  Search,
  Bot,
  CheckCircle2,
  Calendar,
  Bell,
  Zap,
  Clock,
  Target,
  Activity,
  PieChart as PieChartIcon,
  ArrowRight,
  ArrowLeft,
  Sliders,
  Award,
  Shield,
  Heart,
  Briefcase,
  Coffee,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Area,
  AreaChart,
} from 'recharts';
import CSVUpload from '../components/CSVUpload';
import EmployeeTable from '../components/EmployeeTable';
import DepartmentBreakdown from '../components/DepartmentBreakdown';
import { generateEngagementReport } from '../utils/pdfExport';
import Logo from '../components/Logo';
import { Employee, Department } from '../data/employees';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { stats, trendData, employees, departments, isLoading, refreshData, user, setIsAuthenticated } = useApp();
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'employees' | 'departments' | 'slack-demo' | 'interventions' | 'red-flags' | 'predictions' | 'company-insights' | 'live-activity'>('overview');

  const handleLogout = () => {
    setIsAuthenticated(false);
    navigate('/');
  };

  const riskDistribution = [
    { name: 'High Risk', value: stats.highRiskCount, color: '#EF4444' },
    { name: 'Medium Risk', value: stats.mediumRiskCount, color: '#F59E0B' },
    { name: 'Low Risk', value: stats.lowRiskCount, color: '#10B981' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <motion.aside
        style={styles.sidebar}
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div style={styles.sidebarHeader}>
          <Logo variant="white" size="md" />
        </div>

        <nav style={styles.nav}>
          <NavItem
            icon={<BarChart3 size={20} />}
            label="Dashboard"
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          />
          <NavItem
            icon={<Activity size={20} />}
            label="Live Activity"
            active={activeTab === 'live-activity'}
            onClick={() => setActiveTab('live-activity')}
          />
          <NavItem
            icon={<Target size={20} />}
            label="Company Insights"
            active={activeTab === 'company-insights'}
            onClick={() => setActiveTab('company-insights')}
          />
          <NavItem
            icon={<MessageSquare size={20} />}
            label="Integrations"
            onClick={() => navigate('/integrations')}
          />
          <NavItem
            icon={<Zap size={20} />}
            label="Interventions"
            active={activeTab === 'interventions'}
            onClick={() => setActiveTab('interventions')}
          />
          <NavItem
            icon={<MessageSquare size={20} />}
            label="Slack Demo"
            active={activeTab === 'slack-demo'}
            onClick={() => setActiveTab('slack-demo')}
          />
          <NavItem
            icon={<Brain size={20} />}
            label="Agentic AI Demo"
            onClick={() => navigate('/agentic-demo')}
            highlight
          />
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.avatar}>
              {user?.name?.charAt(0) || 'D'}
            </div>
            <div>
              <div style={styles.userName}>{user?.name || 'Demo User'}</div>
              <div style={styles.userEmail}>{user?.email || 'demo@dynami.ai'}</div>
            </div>
          </div>
          <button style={styles.logoutButton} onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Header */}
        <motion.header
          style={styles.header}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <h1 style={styles.pageTitle}>Engagement Dashboard</h1>
            <p style={styles.pageSubtitle}>
              Real-time insights into workforce engagement and risk
            </p>
          </div>

          <div style={styles.headerActions}>
            <div style={styles.searchWrapper}>
              <Search size={18} color="#9CA3AF" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            <motion.button
              style={styles.uploadButton}
              onClick={() => setShowUpload(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Upload size={18} />
              Upload CSV
            </motion.button>

            <motion.button
              style={styles.refreshButton}
              onClick={refreshData}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </motion.button>
          </div>
        </motion.header>

        {/* Loading Overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              style={styles.loadingOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div style={styles.loadingContent}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles size={40} color="#06BCCA" />
                </motion.div>
                <p>Analyzing engagement data...</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Navigation */}
        <div style={styles.tabNav}>
          {(['overview', 'employees', 'departments', 'red-flags', 'predictions'] as const).map((tab) => (
            <button
              key={tab}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab ? styles.tabButtonActive : {}),
              }}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'red-flags' ? 'Red Flags' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div style={styles.content}>
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
        <motion.div
                key="overview"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
                exit="hidden"
                style={{ 
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  position: 'relative',
                }}
              >
              {/* Stats Cards */}
              <div style={styles.statsGrid}>
                <motion.div variants={itemVariants} style={{ ...styles.statCard, ...styles.statCardHighlight }}>
                  <div style={styles.statIcon}>
                    <AlertTriangle size={24} color="#EF4444" />
                  </div>
                  <div style={styles.statContent}>
                    <span style={styles.statValue}>{stats.percentDisengaged}%</span>
                    <span style={styles.statLabel}>Disengaged</span>
                  </div>
                  <div style={styles.statTrend}>
                    <TrendingUp size={16} color="#EF4444" />
                    <span style={{ color: '#EF4444' }}>+2.3%</span>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} style={styles.statCard}>
                  <div style={styles.statIcon}>
                    <Users size={24} color="#69277F" />
                  </div>
                  <div style={styles.statContent}>
                    <span style={styles.statValue}>{stats.highRiskCount}</span>
                    <span style={styles.statLabel}>High Risk Employees</span>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} style={styles.statCard}>
                  <div style={styles.statIcon}>
                    <BarChart3 size={24} color="#06BCCA" />
                  </div>
                  <div style={styles.statContent}>
                    <span style={styles.statValue}>{stats.avgRiskScore}</span>
                      <span style={styles.statLabel}>Avg DDI</span>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} style={styles.statCard}>
                  <div style={styles.statIcon}>
                    <DollarSign size={24} color="#FF9C0D" />
                  </div>
                  <div style={styles.statContent}>
                    <span style={styles.statValue}>${(stats.estimatedCost / 1000).toFixed(0)}K</span>
                    <span style={styles.statLabel}>Est. Disengagement Cost</span>
                  </div>
                </motion.div>
              </div>

              {/* Charts Row */}
              <div style={styles.chartsGrid}>
                {/* Trend Chart */}
                <motion.div variants={itemVariants} style={styles.chartCard}>
                  <h3 style={styles.chartTitle}>Engagement Risk Trend</h3>
                  <p style={styles.chartSubtitle}>12-week rolling view of risk distribution</p>
                  <div style={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                        <Tooltip
                          contentStyle={{
                            borderRadius: '12px',
                            border: 'none',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="highRisk"
                          stroke="#EF4444"
                          strokeWidth={3}
                          dot={{ fill: '#EF4444', strokeWidth: 2 }}
                          name="High Risk"
                        />
                        <Line
                          type="monotone"
                          dataKey="mediumRisk"
                          stroke="#F59E0B"
                          strokeWidth={3}
                          dot={{ fill: '#F59E0B', strokeWidth: 2 }}
                          name="Medium Risk"
                        />
                        <Line
                          type="monotone"
                          dataKey="lowRisk"
                          stroke="#10B981"
                          strokeWidth={3}
                          dot={{ fill: '#10B981', strokeWidth: 2 }}
                          name="Low Risk"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>

                {/* Risk Distribution */}
                <motion.div variants={itemVariants} style={styles.chartCard}>
                  <h3 style={styles.chartTitle}>Risk Distribution</h3>
                  <p style={styles.chartSubtitle}>Current workforce breakdown by risk level</p>
                  <div style={styles.pieChartContainer}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={riskDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {riskDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={styles.legendContainer}>
                      {riskDistribution.map((item) => (
                        <div key={item.name} style={styles.legendItem}>
                          <div
                            style={{ ...styles.legendDot, backgroundColor: item.color }}
                          />
                          <span style={styles.legendLabel}>{item.name}</span>
                          <span style={styles.legendValue}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Behavioral Flags */}
              <motion.div variants={itemVariants} style={styles.flagsCard}>
                <h3 style={styles.chartTitle}>Top Behavioral Red Flags</h3>
                <p style={styles.chartSubtitle}>Most common disengagement indicators across your workforce</p>
                <div style={styles.flagsList}>
                  {stats.topBehavioralFlags.map((flag, index) => (
                    <motion.div
                      key={flag.flag}
                      style={styles.flagItem}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div style={styles.flagRank}>{index + 1}</div>
                      <span style={styles.flagText}>{flag.flag}</span>
                      <div style={styles.flagBarContainer}>
                        <motion.div
                          style={styles.flagBar}
                          initial={{ width: 0 }}
                          animate={{ width: `${(flag.count / stats.totalEmployees) * 100 * 3}%` }}
                          transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                        />
                      </div>
                      <span style={styles.flagCount}>{flag.count}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Quick Actions */}
              <motion.div variants={itemVariants} style={styles.actionsCard}>
                <h3 style={styles.chartTitle}>Quick Actions</h3>
                <div style={styles.actionsGrid}>
                  <button
                    style={styles.actionButton}
                    onClick={() => navigate('/integrations')}
                  >
                    <MessageSquare size={24} color="#06BCCA" />
                    <span>View Integrations</span>
                    <ChevronRight size={18} color="#9CA3AF" />
                  </button>
                  <button
                    style={styles.actionButton}
                    onClick={() => navigate('/agentic-demo')}
                  >
                    <Brain size={24} color="#FF68C5" />
                    <span>Agentic AI Demo</span>
                    <ChevronRight size={18} color="#9CA3AF" />
                  </button>
                  <button 
                    style={styles.actionButton}
                    onClick={() => generateEngagementReport({
                      stats,
                      departments,
                      highRiskEmployees: employees.filter(e => e.riskLevel === 'High'),
                      generatedDate: new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }),
                    })}
                  >
                    <FileDown size={24} color="#69277F" />
                    <span>Export Report</span>
                    <ChevronRight size={18} color="#9CA3AF" />
                  </button>
                </div>
              </motion.div>

              {/* AI Intervention Impact Score */}
              <motion.div variants={itemVariants} style={styles.impactScoreCard}>
                <div style={styles.impactScoreHeader}>
                  <div style={styles.impactScoreIcon}>
                    <Target size={24} color="#69277F" />
                  </div>
                  <div>
                    <h3 style={styles.impactScoreTitle}>AI Intervention Impact Score</h3>
                    <p style={styles.impactScoreSubtitle}>Proprietary score showing agentic AI impact</p>
                  </div>
                </div>
                <div style={styles.impactScoreGrid}>
                  <div style={styles.impactScoreItem}>
                    <div style={styles.impactScoreValue}>+12%</div>
                    <div style={styles.impactScoreLabel}>Projected engagement improvement</div>
                  </div>
                  <div style={styles.impactScoreItem}>
                    <div style={styles.impactScoreValue}>34</div>
                    <div style={styles.impactScoreLabel}>Interventions executed</div>
                  </div>
                  <div style={styles.impactScoreItem}>
                    <div style={styles.impactScoreValue}>78%</div>
                    <div style={styles.impactScoreLabel}>Manager follow-through rate</div>
                  </div>
                  <div style={styles.impactScoreItem}>
                    <div style={styles.impactScoreValue}>8</div>
                    <div style={styles.impactScoreLabel}>Recovered employees</div>
                  </div>
                </div>
              </motion.div>

              </motion.div>
          )}

          {activeTab === 'employees' && (
              <motion.div
                key="employees"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
            <EmployeeTable
              employees={employees}
              searchQuery={searchQuery}
            />
              </motion.div>
          )}

          {activeTab === 'departments' && (
              <motion.div
                key="departments"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
            <DepartmentBreakdown departments={departments} />
              </motion.div>
          )}

            {activeTab === 'slack-demo' && (
              <motion.div
                key="slack-demo"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <SlackDemoTab />
        </motion.div>
            )}

            {activeTab === 'interventions' && (
              <motion.div
                key="interventions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <InterventionsTab employees={employees} />
              </motion.div>
            )}

            {activeTab === 'red-flags' && (
              <motion.div
                key="red-flags"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <RedFlagsTab employees={employees} />
              </motion.div>
            )}

            {activeTab === 'predictions' && (
              <motion.div
                key="predictions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <PredictionsTab employees={employees} />
              </motion.div>
            )}

            {activeTab === 'company-insights' && (
              <motion.div
                key="company-insights"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <CompanyInsightsTab employees={employees} departments={departments} />
              </motion.div>
            )}

            {activeTab === 'live-activity' && (
              <motion.div
                key="live-activity"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <LiveActivityTab />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* CSV Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <CSVUpload onClose={() => setShowUpload(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

const NavItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  highlight?: boolean;
  onClick: () => void;
}> = ({ icon, label, active, highlight, onClick }) => (
  <motion.button
    style={{
      ...styles.navItem,
      ...(active ? styles.navItemActive : {}),
      ...(highlight ? styles.navItemHighlight : {}),
    }}
    onClick={onClick}
    whileHover={{ x: 4 }}
    whileTap={{ scale: 0.98 }}
  >
    {icon}
    <span>{label}</span>
    {highlight && <Sparkles size={14} color="#FFC800" />}
  </motion.button>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F8F9FC',
  },
  sidebar: {
    width: '260px',
    background: 'linear-gradient(180deg, #431652 0%, #2D0E38 100%)',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    height: '100vh',
    zIndex: 100,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0 12px',
    marginBottom: '32px',
  },
  logoText: {
    color: 'white',
    fontSize: '24px',
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.2s ease',
  },
  navItemActive: {
    background: 'rgba(6, 188, 202, 0.2)',
    color: 'white',
  },
  navItemHighlight: {
    background: 'linear-gradient(135deg, rgba(255, 104, 197, 0.2) 0%, rgba(255, 200, 0, 0.2) 100%)',
    color: 'white',
  },
  sidebarFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 12px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #06BCCA 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
  },
  userName: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
  },
  userEmail: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
  },
  logoutButton: {
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    background: 'transparent',
    border: 'none',
  },
  main: {
    flex: 1,
    marginLeft: '260px',
    padding: '24px 32px',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '4px',
  },
  pageSubtitle: {
    fontSize: '15px',
    color: '#6B7280',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'white',
    borderRadius: '12px',
    border: '2px solid #E5E7EB',
    minWidth: '240px',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    flex: 1,
    background: 'transparent',
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    background: 'white',
    borderRadius: '12px',
    border: '2px solid #E5E7EB',
    cursor: 'pointer',
    color: '#6B7280',
  },
  loadingOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(255,255,255,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    marginLeft: '260px',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    color: '#69277F',
    fontWeight: 500,
  },
  tabNav: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '2px solid #E5E7EB',
    paddingBottom: '12px',
  },
  tabButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
    background: 'transparent',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  tabButtonActive: {
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    transition: 'all 0.3s ease',
  },
  statCardHighlight: {
    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.1) 100%)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(105, 39, 127, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    fontFamily: 'var(--font-heading)',
  },
  statLabel: {
    fontSize: '13px',
    color: '#6B7280',
  },
  statTrend: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 500,
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
  },
  chartCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '4px',
  },
  chartSubtitle: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '20px',
  },
  chartContainer: {
    width: '100%',
  },
  pieChartContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '4px',
  },
  legendLabel: {
    fontSize: '14px',
    color: '#6B7280',
    flex: 1,
  },
  legendValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  flagsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  flagsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  flagRank: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
  },
  flagText: {
    flex: 1,
    fontSize: '14px',
    color: '#374151',
    minWidth: '200px',
  },
  flagBarContainer: {
    flex: 2,
    height: '8px',
    background: '#F3F4F6',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  flagBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #69277F 0%, #FF68C5 100%)',
    borderRadius: '4px',
  },
  flagCount: {
    width: '40px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  actionsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    background: '#F9FAFB',
    borderRadius: '12px',
    border: '2px solid #E5E7EB',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 500,
    color: '#374151',
    transition: 'all 0.2s ease',
  },
  // New Feature Styles
  impactScoreCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  impactScoreHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  impactScoreIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(105, 39, 127, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  impactScoreTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  impactScoreSubtitle: {
    fontSize: '13px',
    color: '#6B7280',
    margin: 0,
  },
  impactScoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  impactScoreItem: {
    textAlign: 'center',
    padding: '20px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  impactScoreValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '8px',
  },
  impactScoreLabel: {
    fontSize: '13px',
    color: '#6B7280',
  },
  timelineCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  timelineHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  timelineTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  timelineContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative',
    paddingLeft: '24px',
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    position: 'relative',
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    position: 'absolute',
    left: '-18px',
    border: '3px solid white',
    boxShadow: '0 0 0 2px currentColor',
  },
  timelineContent: {
    flex: 1,
  },
  timelineDay: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '4px',
  },
  timelineAction: {
    fontSize: '14px',
    color: '#6B7280',
  },
  heatmapRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
  },
  heatmapCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  heatmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '20px',
  },
  heatmapCell: {
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  heatmapCellHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  heatmapCellName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  heatmapCellBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  heatmapCellMetrics: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  heatmapMetric: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
  },
  heatmapMetricLabel: {
    color: '#6B7280',
  },
  heatmapMetricValue: {
    fontWeight: 600,
    color: '#1F2937',
  },
  predictionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  predictionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  predictionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  predictionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  predictionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  predictionValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '4px',
  },
  predictionLabel: {
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: 1.5,
  },
  recoveryRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  recoveredCard: {
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '2px solid rgba(16, 185, 129, 0.2)',
  },
  recoveredHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  recoveredTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  recoveredStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  recoveredStat: {
    textAlign: 'center',
    padding: '16px',
    background: 'white',
    borderRadius: '12px',
  },
  recoveredStatValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#10B981',
    marginBottom: '4px',
  },
  recoveredStatLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  managerEngagementCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  managerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  managerItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
  },
  managerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  managerAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '16px',
  },
  managerName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '2px',
  },
  managerStatus: {
    fontSize: '12px',
    color: '#6B7280',
  },
  managerScore: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '120px',
  },
  managerScoreBar: {
    flex: 1,
    height: '8px',
    background: '#E5E7EB',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  managerScoreFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  managerScoreValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    minWidth: '40px',
    textAlign: 'right',
  },
  analysisRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  workloadCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  workloadMetrics: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  workloadMetric: {
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  workloadMetricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  workloadMetricLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  workloadMetricValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  workloadMetricDesc: {
    fontSize: '12px',
    color: '#6B7280',
  },
  rootCauseCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  rootCauseWheel: {
    marginTop: '20px',
  },
  simulatorRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
  },
  simulatorCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  simulatorContent: {
    marginTop: '20px',
  },
  sliderContainer: {
    marginBottom: '24px',
  },
  sliderLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '12px',
  },
  sliderValue: {
    color: '#69277F',
    fontWeight: 700,
  },
  slider: {
    width: '100%',
    height: '8px',
    borderRadius: '4px',
    background: '#E5E7EB',
    outline: 'none',
    appearance: 'none',
  },
  savingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  savingsItem: {
    textAlign: 'center',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  savingsIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px',
    color: '#69277F',
  },
  savingsValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  savingsLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  totalSavings: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(105, 39, 127, 0.1) 0%, rgba(105, 39, 127, 0.2) 100%)',
    borderRadius: '12px',
    marginTop: '16px',
  },
  totalSavingsLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
  },
  totalSavingsValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#69277F',
  },
  autonomyCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  autonomyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  autonomyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  autonomyContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  autonomyStat: {
    textAlign: 'center',
    padding: '20px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  autonomyValue: {
    fontSize: '48px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '8px',
  },
  autonomyLabel: {
    fontSize: '14px',
    color: '#6B7280',
  },
  autonomyBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  autonomyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  autonomyBar: {
    flex: 1,
    height: '12px',
    background: '#E5E7EB',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  autonomyBarFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease',
  },
  autonomyItemLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#4B5563',
    minWidth: '120px',
  },
  liveRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  liveActivityCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  liveActivityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  liveActivityTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  liveActivityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  liveActivityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
  },
  liveActivityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#06BCCA',
    marginTop: '6px',
    flexShrink: 0,
    animation: 'pulse 2s infinite',
  },
  liveActivityContent: {
    flex: 1,
  },
  liveActivityMessage: {
    fontSize: '14px',
    color: '#374151',
    marginBottom: '4px',
  },
  liveActivityTime: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  sentimentCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  sentimentChart: {
    marginTop: '20px',
  },
  sentimentLegend: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
  },
  sentimentLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#6B7280',
  },
  sentimentLegendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '4px',
  },
  stressRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  stressCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  stressMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginTop: '20px',
  },
  stressMetric: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  stressMetricIcon: {
    color: '#69277F',
  },
  stressMetricContent: {
    flex: 1,
  },
  stressMetricLabel: {
    fontSize: '13px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  stressMetricValue: {
    fontSize: '20px',
    fontWeight: 700,
  },
  recommendationsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  recommendationsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  recommendationsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  recommendationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  recommendationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
  },
  recommendationText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.5,
  },
};

// Interventions Tab Component
interface InterventionsTabProps {
  employees: Employee[];
}

const InterventionsTab: React.FC<InterventionsTabProps> = ({ employees }) => {
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  interface Intervention {
    id: string;
    type: 'meeting' | 'notification' | 'check-in' | 'score-update' | 'action-plan';
    title: string;
    description: string;
    employeeName: string;
    employeeId: string;
    managerName: string;
    date: string;
    time: string;
    status: 'completed' | 'in-progress' | 'scheduled';
    impact?: string;
    ddiChange?: string;
  }

  // Generate sample interventions based on employees
  const generateInterventions = (): Intervention[] => {
    const interventions: Intervention[] = [];
    const highRiskEmployees = employees.filter(e => e.riskLevel === 'High').slice(0, 8);
    const mediumRiskEmployees = employees.filter(e => e.riskLevel === 'Medium').slice(0, 4);

    highRiskEmployees.forEach((emp, index) => {
      const daysAgo = index * 2;
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      
      interventions.push({
        id: `int-${emp.id}-1`,
        type: 'check-in',
        title: 'Proactive Check-in',
        description: `Dynami initiated a check-in conversation via Slack to assess engagement levels`,
        employeeName: emp.name,
        employeeId: emp.id,
        managerName: emp.manager,
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: '10:32 AM',
        status: 'completed',
        impact: 'Employee expressed workload concerns',
      });

      if (index < 5) {
        interventions.push({
          id: `int-${emp.id}-2`,
          type: 'notification',
          title: 'Manager Alert Sent',
          description: `Alerted ${emp.manager} about engagement concerns and provided intervention recommendations`,
          employeeName: emp.name,
          employeeId: emp.id,
          managerName: emp.manager,
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          time: '10:38 AM',
          status: 'completed',
        });
      }

      if (index < 3) {
        const meetingDate = new Date(date);
        meetingDate.setDate(meetingDate.getDate() + 1);
        interventions.push({
          id: `int-${emp.id}-3`,
          type: 'meeting',
          title: '1:1 Meeting Scheduled',
          description: `Automatically scheduled 1:1 between ${emp.name} and ${emp.manager}`,
          employeeName: emp.name,
          employeeId: emp.id,
          managerName: emp.manager,
          date: meetingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          time: '2:00 PM',
          status: index === 0 ? 'scheduled' : 'completed',
        });
      }

      if (index < 4) {
        const scoreDate = new Date(date);
        scoreDate.setDate(scoreDate.getDate() + 1);
        interventions.push({
          id: `int-${emp.id}-4`,
          type: 'score-update',
          title: 'DDI Score Updated',
          description: `DDI score adjusted based on intervention progress and engagement signals`,
          employeeName: emp.name,
          employeeId: emp.id,
          managerName: emp.manager,
          date: scoreDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          time: '11:15 AM',
          status: 'completed',
          ddiChange: `${emp.riskScore} → ${Math.max(emp.riskScore - 6, 30)}`,
        });
      }
    });

    mediumRiskEmployees.forEach((emp, index) => {
      const daysAgo = index * 3 + 1;
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      
      interventions.push({
        id: `int-${emp.id}-1`,
        type: 'check-in',
        title: 'Routine Check-in',
        description: `Periodic engagement check-in to monitor employee wellbeing`,
        employeeName: emp.name,
        employeeId: emp.id,
        managerName: emp.manager,
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: '9:45 AM',
        status: 'completed',
      });
    });

    return interventions.sort((a, b) => {
      const dateA = new Date(a.date + ' ' + a.time);
      const dateB = new Date(b.date + ' ' + b.time);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const [interventions] = useState<Intervention[]>(generateInterventions());

  const filteredInterventions = interventions.filter(int => {
    const matchesType = filterType === 'all' || int.type === filterType;
    const matchesStatus = filterStatus === 'all' || int.status === filterStatus;
    return matchesType && matchesStatus;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'meeting':
        return <Calendar size={20} />;
      case 'notification':
        return <Bell size={20} />;
      case 'check-in':
        return <MessageSquare size={20} />;
      case 'score-update':
        return <BarChart3 size={20} />;
      case 'action-plan':
        return <Brain size={20} />;
      default:
        return <Zap size={20} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'meeting':
        return '#10B981';
      case 'notification':
        return '#FF68C5';
      case 'check-in':
        return '#06BCCA';
      case 'score-update':
        return '#69277F';
      case 'action-plan':
        return '#FF9C0D';
      default:
        return '#6B7280';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return { bg: '#D1FAE5', color: '#059669', text: 'Completed' };
      case 'in-progress':
        return { bg: '#FEF3C7', color: '#D97706', text: 'In Progress' };
      case 'scheduled':
        return { bg: '#DBEAFE', color: '#2563EB', text: 'Scheduled' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', text: status };
    }
  };

  const typeCounts = {
    all: interventions.length,
    meeting: interventions.filter(i => i.type === 'meeting').length,
    notification: interventions.filter(i => i.type === 'notification').length,
    'check-in': interventions.filter(i => i.type === 'check-in').length,
    'score-update': interventions.filter(i => i.type === 'score-update').length,
    'action-plan': interventions.filter(i => i.type === 'action-plan').length,
  };

  const statusCounts = {
    all: interventions.length,
    completed: interventions.filter(i => i.status === 'completed').length,
    'in-progress': interventions.filter(i => i.status === 'in-progress').length,
    scheduled: interventions.filter(i => i.status === 'scheduled').length,
  };

  return (
    <div style={interventionsStyles.container}>
      {/* Header Stats */}
      <motion.div
        style={interventionsStyles.statsGrid}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={interventionsStyles.statCard}>
          <div style={interventionsStyles.statValue}>{interventions.length}</div>
          <div style={interventionsStyles.statLabel}>Total Interventions</div>
        </div>
        <div style={interventionsStyles.statCard}>
          <div style={interventionsStyles.statValue}>
            {interventions.filter(i => i.status === 'completed').length}
          </div>
          <div style={interventionsStyles.statLabel}>Completed</div>
        </div>
        <div style={interventionsStyles.statCard}>
          <div style={interventionsStyles.statValue}>
            {interventions.filter(i => i.status === 'scheduled').length}
          </div>
          <div style={interventionsStyles.statLabel}>Scheduled</div>
        </div>
        <div style={interventionsStyles.statCard}>
          <div style={interventionsStyles.statValue}>
            {new Set(interventions.map(i => i.employeeId)).size}
          </div>
          <div style={interventionsStyles.statLabel}>Employees Supported</div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        style={interventionsStyles.filtersSection}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div style={interventionsStyles.filterGroup}>
          <span style={interventionsStyles.filterLabel}>Type:</span>
          {(['all', 'check-in', 'notification', 'meeting', 'score-update'] as const).map((type) => (
            <button
              key={type}
              style={{
                ...interventionsStyles.filterButton,
                ...(filterType === type ? interventionsStyles.filterButtonActive : {}),
              }}
              onClick={() => setFilterType(type)}
            >
              {type === 'all' ? 'All' : type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              <span style={interventionsStyles.filterCount}>({typeCounts[type]})</span>
            </button>
          ))}
        </div>
        <div style={interventionsStyles.filterGroup}>
          <span style={interventionsStyles.filterLabel}>Status:</span>
          {(['all', 'completed', 'in-progress', 'scheduled'] as const).map((status) => (
            <button
              key={status}
              style={{
                ...interventionsStyles.filterButton,
                ...(filterStatus === status ? interventionsStyles.filterButtonActive : {}),
              }}
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' ? 'All' : status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              <span style={interventionsStyles.filterCount}>({statusCounts[status]})</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Interventions List */}
      <motion.div
        style={interventionsStyles.interventionsList}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {filteredInterventions.map((intervention, index) => {
          const typeColor = getTypeColor(intervention.type);
          const statusBadge = getStatusBadge(intervention.status);
          const employee = employees.find(e => e.id === intervention.employeeId);

          return (
            <motion.div
              key={intervention.id}
              style={interventionsStyles.interventionCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              whileHover={{ y: -2, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
            >
              <div style={interventionsStyles.interventionHeader}>
                <div
                  style={{
                    ...interventionsStyles.typeIcon,
                    background: `${typeColor}15`,
                    color: typeColor,
                  }}
                >
                  {getTypeIcon(intervention.type)}
                </div>
                <div style={interventionsStyles.interventionInfo}>
                  <div style={interventionsStyles.interventionTitleRow}>
                    <h3 style={interventionsStyles.interventionTitle}>{intervention.title}</h3>
                    <span
                      style={{
                        ...interventionsStyles.statusBadge,
                        background: statusBadge.bg,
                        color: statusBadge.color,
                      }}
                    >
                      {statusBadge.text}
                    </span>
                  </div>
                  <div style={interventionsStyles.interventionMeta}>
                    <span style={interventionsStyles.metaItem}>
                      <User size={14} />
                      {intervention.employeeName}
                    </span>
                    <span style={interventionsStyles.metaItem}>
                      <Users size={14} />
                      {intervention.managerName}
                    </span>
                    <span style={interventionsStyles.metaItem}>
                      <Calendar size={14} />
                      {intervention.date} at {intervention.time}
                    </span>
                  </div>
                </div>
              </div>

              <p style={interventionsStyles.interventionDescription}>{intervention.description}</p>

              {(intervention.impact || intervention.ddiChange) && (
                <div style={interventionsStyles.interventionDetails}>
                  {intervention.ddiChange && (
                    <div style={interventionsStyles.detailItem}>
                      <BarChart3 size={16} color="#69277F" />
                      <span style={interventionsStyles.detailLabel}>DDI Change:</span>
                      <span style={interventionsStyles.detailValue}>{intervention.ddiChange}</span>
                    </div>
                  )}
                  {intervention.impact && (
                    <div style={interventionsStyles.detailItem}>
                      <Zap size={16} color="#06BCCA" />
                      <span style={interventionsStyles.detailLabel}>Impact:</span>
                      <span style={interventionsStyles.detailValue}>{intervention.impact}</span>
                    </div>
                  )}
                </div>
              )}

              {employee && (
                <div style={interventionsStyles.employeeBadge}>
                  <div
                    style={{
                      ...interventionsStyles.riskBadge,
                      background:
                        employee.riskLevel === 'High'
                          ? '#FEE2E2'
                          : employee.riskLevel === 'Medium'
                          ? '#FEF3C7'
                          : '#D1FAE5',
                      color:
                        employee.riskLevel === 'High'
                          ? '#DC2626'
                          : employee.riskLevel === 'Medium'
                          ? '#D97706'
                          : '#059669',
                    }}
                  >
                    {employee.riskLevel} Risk • DDI: {employee.riskScore}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </motion.div>

      {filteredInterventions.length === 0 && (
        <motion.div
          style={interventionsStyles.emptyState}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Zap size={48} color="#D1D5DB" />
          <h3 style={interventionsStyles.emptyTitle}>No interventions found</h3>
          <p style={interventionsStyles.emptyText}>
            Try adjusting your filters to see more results
          </p>
        </motion.div>
      )}
    </div>
  );
};

const interventionsStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#6B7280',
  },
  filtersSection: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginRight: '8px',
  },
  filterButton: {
    padding: '8px 16px',
    background: '#F3F4F6',
    border: '2px solid transparent',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#6B7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderColor: '#69277F',
  },
  filterCount: {
    fontSize: '11px',
    opacity: 0.7,
  },
  interventionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  interventionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    border: '1px solid #E5E7EB',
  },
  interventionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '12px',
  },
  typeIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  interventionInfo: {
    flex: 1,
  },
  interventionTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  interventionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  interventionMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    fontSize: '13px',
    color: '#6B7280',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  interventionDescription: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  interventionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    marginBottom: '12px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  detailLabel: {
    fontWeight: 600,
    color: '#6B7280',
  },
  detailValue: {
    color: '#1F2937',
    fontWeight: 500,
  },
  employeeBadge: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  riskBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '16px 0 8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
};

// Predictions Tab Component
interface PredictionsTabProps {
  employees: Employee[];
}

const PredictionsTab: React.FC<PredictionsTabProps> = ({ employees }) => {
  const [sliderValue, setSliderValue] = useState(15);

  const calculateSavings = (reduction: number) => {
    const base = {
      attrition: 1200000,
      productivity: 850000,
      performance: 420000,
      burnout: 180000,
      hiring: 640000,
    };
    const multiplier = reduction / 15;
    return {
      attrition: base.attrition * multiplier,
      productivity: base.productivity * multiplier,
      performance: base.performance * multiplier,
      burnout: base.burnout * multiplier,
      hiring: base.hiring * multiplier,
    };
  };

  const savings = calculateSavings(sliderValue);
  const totalSavings = Object.values(savings).reduce((sum, val) => sum + val, 0);

  return (
    <div style={predictionsStyles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={predictionsStyles.header}>
          <div>
            <h2 style={predictionsStyles.title}>Predictive Analytics</h2>
            <p style={predictionsStyles.subtitle}>
              AI-powered predictions and financial impact modeling
            </p>
          </div>
        </div>

        {/* Prediction Window */}
        <motion.div
          style={predictionsStyles.predictionCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={predictionsStyles.predictionHeader}>
            <Brain size={24} color="#FF68C5" />
            <h3 style={predictionsStyles.predictionTitle}>Prediction Window</h3>
          </div>
          <div style={predictionsStyles.predictionContent}>
            <div style={predictionsStyles.predictionItem}>
              <AlertTriangle size={18} color="#EF4444" />
              <div>
                <div style={predictionsStyles.predictionValue}>20</div>
                <div style={predictionsStyles.predictionLabel}>employees predicted to become high-risk within 2 weeks</div>
              </div>
            </div>
            <div style={predictionsStyles.predictionItem}>
              <CheckCircle2 size={18} color="#10B981" />
              <div>
                <div style={predictionsStyles.predictionValue}>6</div>
                <div style={predictionsStyles.predictionLabel}>employees predicted to recover with scheduled interventions</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Financial Impact Simulator */}
        <motion.div
          style={predictionsStyles.simulatorCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 style={predictionsStyles.chartTitle}>Financial Impact Simulator</h3>
          <p style={predictionsStyles.chartSubtitle}>Projected savings from engagement improvements</p>
          <div style={predictionsStyles.simulatorContent}>
            <div style={predictionsStyles.sliderContainer}>
              <label style={predictionsStyles.sliderLabel}>
                Reduce disengagement by: <span style={predictionsStyles.sliderValue}>{sliderValue}%</span>
              </label>
              <input
                type="range"
                min="1"
                max="20"
                value={sliderValue}
                onChange={(e) => setSliderValue(Number(e.target.value))}
                style={predictionsStyles.slider}
              />
            </div>
            <div style={predictionsStyles.savingsGrid}>
              {[
                { label: 'Attrition Cost', value: savings.attrition, icon: <Users size={20} /> },
                { label: 'Lost Productivity', value: savings.productivity, icon: <Activity size={20} /> },
                { label: 'Performance Decline', value: savings.performance, icon: <TrendingDown size={20} /> },
                { label: 'Manager Burnout', value: savings.burnout, icon: <Coffee size={20} /> },
                { label: 'Hiring Cost Saved', value: savings.hiring, icon: <Briefcase size={20} /> },
              ].map((item, index) => (
                <div key={index} style={predictionsStyles.savingsItem}>
                  <div style={predictionsStyles.savingsIcon}>{item.icon}</div>
                  <div style={predictionsStyles.savingsValue}>
                    ${(item.value / 1000).toFixed(0)}K
                  </div>
                  <div style={predictionsStyles.savingsLabel}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={predictionsStyles.totalSavings}>
              <span style={predictionsStyles.totalSavingsLabel}>Total Projected Savings:</span>
              <span style={predictionsStyles.totalSavingsValue}>
                ${(totalSavings / 1000000).toFixed(2)}M
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const predictionsStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  predictionCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  predictionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  predictionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  predictionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  predictionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  predictionValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '4px',
  },
  predictionLabel: {
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: 1.5,
  },
  simulatorCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '4px',
  },
  chartSubtitle: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '20px',
  },
  simulatorContent: {
    marginTop: '20px',
  },
  sliderContainer: {
    marginBottom: '24px',
  },
  sliderLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '12px',
  },
  sliderValue: {
    color: '#69277F',
    fontWeight: 700,
  },
  slider: {
    width: '100%',
    height: '8px',
    borderRadius: '4px',
    background: '#E5E7EB',
    outline: 'none',
    appearance: 'none',
  },
  savingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  savingsItem: {
    textAlign: 'center',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  savingsIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px',
    color: '#69277F',
  },
  savingsValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  savingsLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  totalSavings: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(105, 39, 127, 0.1) 0%, rgba(105, 39, 127, 0.2) 100%)',
    borderRadius: '12px',
    marginTop: '16px',
  },
  totalSavingsLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
  },
  totalSavingsValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#69277F',
  },
};

// Company Insights Tab Component
interface CompanyInsightsTabProps {
  employees: Employee[];
  departments: Department[];
}

const CompanyInsightsTab: React.FC<CompanyInsightsTabProps> = ({ employees, departments }) => {
  return (
    <div style={insightsStyles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={insightsStyles.header}>
          <div>
            <h2 style={insightsStyles.title}>Company Insights</h2>
            <p style={insightsStyles.subtitle}>
              Organizational-level insights and recommendations
            </p>
          </div>
        </div>

        {/* Organizational Prescription & Calendar Stress Row */}
        <div style={insightsStyles.topRow}>
          {/* Recommended Actions for Organisation */}
          <motion.div
            style={insightsStyles.recommendationsCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div style={insightsStyles.recommendationsHeader}>
              <Target size={24} color="#69277F" />
              <h3 style={insightsStyles.recommendationsTitle}>Organisational Prescription</h3>
            </div>
            <div style={insightsStyles.recommendationsList}>
              {[
                'Reduce meeting load in Operations by 15%',
                'Introduce weekly async check-ins for Engineering',
                'Improve manager responsiveness to interventions',
                'Update wellbeing policy triggers',
              ].map((rec, index) => (
                <div key={index} style={insightsStyles.recommendationItem}>
                  <ArrowRight size={16} color="#69277F" />
                  <span style={insightsStyles.recommendationText}>{rec}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Calendar Stress Indicator */}
          <motion.div
            style={insightsStyles.stressCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 style={insightsStyles.chartTitle}>Calendar Stress Indicator</h3>
            <p style={insightsStyles.chartSubtitle}>Workload and meeting patterns</p>
            <div style={insightsStyles.stressMetrics}>
              {[
                { label: 'Meeting Overload', value: '42%', color: '#EF4444', icon: <Calendar size={18} /> },
                { label: 'Focus Time Shortage', value: '38%', color: '#F59E0B', icon: <Clock size={18} /> },
                { label: 'Context Switching', value: '56%', color: '#EF4444', icon: <Activity size={18} /> },
                { label: 'After-Hours Work', value: '28%', color: '#F59E0B', icon: <Coffee size={18} /> },
              ].map((item, index) => (
                <div key={index} style={insightsStyles.stressMetric}>
                  <div style={insightsStyles.stressMetricIcon}>{item.icon}</div>
                  <div style={insightsStyles.stressMetricContent}>
                    <div style={insightsStyles.stressMetricLabel}>{item.label}</div>
                    <div style={{ ...insightsStyles.stressMetricValue, color: item.color }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Engagement Sentiment & Workload Pulse Row */}
        <div style={insightsStyles.middleRow}>
          {/* Engagement Sentiment Overlay */}
          <motion.div
            style={insightsStyles.sentimentCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 style={insightsStyles.chartTitle}>Engagement Sentiment Overlay</h3>
            <p style={insightsStyles.chartSubtitle}>Real-time sentiment from Slack/Teams</p>
            <div style={insightsStyles.sentimentChart}>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={[
                  { time: 'Mon', positive: 65, negative: 15, neutral: 20 },
                  { time: 'Tue', positive: 70, negative: 12, neutral: 18 },
                  { time: 'Wed', positive: 68, negative: 18, neutral: 14 },
                  { time: 'Thu', positive: 72, negative: 10, neutral: 18 },
                  { time: 'Fri', positive: 75, negative: 8, neutral: 17 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="time" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <Tooltip />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={insightsStyles.sentimentLegend}>
              <div style={insightsStyles.sentimentLegendItem}>
                <div style={{ ...insightsStyles.sentimentLegendDot, background: '#10B981' }} />
                <span>Positive</span>
              </div>
              <div style={insightsStyles.sentimentLegendItem}>
                <div style={{ ...insightsStyles.sentimentLegendDot, background: '#F59E0B' }} />
                <span>Neutral</span>
              </div>
              <div style={insightsStyles.sentimentLegendItem}>
                <div style={{ ...insightsStyles.sentimentLegendDot, background: '#EF4444' }} />
                <span>Negative</span>
              </div>
            </div>
          </motion.div>

          {/* Workload Pulse Indicator */}
          <motion.div
            style={insightsStyles.workloadCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 style={insightsStyles.chartTitle}>Workload Pulse Indicator</h3>
            <p style={insightsStyles.chartSubtitle}>Workload correlation to disengagement</p>
            <div style={insightsStyles.workloadMetrics}>
              <div style={insightsStyles.workloadMetric}>
                <div style={insightsStyles.workloadMetricHeader}>
                  <Briefcase size={18} color="#EF4444" />
                  <span style={insightsStyles.workloadMetricLabel}>Workload Overload</span>
                </div>
                <div style={insightsStyles.workloadMetricValue}>32%</div>
                <div style={insightsStyles.workloadMetricDesc}>of high-risk employees</div>
              </div>
              <div style={insightsStyles.workloadMetric}>
                <div style={insightsStyles.workloadMetricHeader}>
                  <Activity size={18} color="#F59E0B" />
                  <span style={insightsStyles.workloadMetricLabel}>Context-Switch Spikes</span>
                </div>
                <div style={insightsStyles.workloadMetricValue}>68%</div>
                <div style={insightsStyles.workloadMetricDesc}>showing increased switching</div>
              </div>
              <div style={insightsStyles.workloadMetric}>
                <div style={insightsStyles.workloadMetricHeader}>
                  <MessageSquare size={18} color="#06BCCA" />
                  <span style={insightsStyles.workloadMetricLabel}>Slack Activity Drop</span>
                </div>
                <div style={insightsStyles.workloadMetricValue}>24%</div>
                <div style={insightsStyles.workloadMetricDesc}>decrease in engagement</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Manager Engagement Score */}
        <motion.div
          style={insightsStyles.managerEngagementCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 style={insightsStyles.chartTitle}>Manager Engagement Score</h3>
          <p style={insightsStyles.chartSubtitle}>Proactive engagement follow-through</p>
          <div style={insightsStyles.managerList}>
            {[
              { name: 'Sarah Chen', score: 92, status: 'excellent' },
              { name: 'Michael Rodriguez', score: 78, status: 'good' },
              { name: 'Emily Watson', score: 15, status: 'risk' },
              { name: 'David Kim', score: 85, status: 'good' },
            ].map((manager, index) => (
              <div key={index} style={insightsStyles.managerItem}>
                <div style={insightsStyles.managerInfo}>
                  <div style={insightsStyles.managerAvatar}>{manager.name.charAt(0)}</div>
                  <div>
                    <div style={insightsStyles.managerName}>{manager.name}</div>
                    <div style={insightsStyles.managerStatus}>
                      {manager.status === 'excellent' && '✓ Excellent follow-through'}
                      {manager.status === 'good' && '✓ Good engagement'}
                      {manager.status === 'risk' && '⚠ Needs attention'}
                    </div>
                  </div>
                </div>
                <div style={insightsStyles.managerScore}>
                  <div style={insightsStyles.managerScoreBar}>
                    <div style={{
                      ...insightsStyles.managerScoreFill,
                      width: `${manager.score}%`,
                      background: manager.score >= 70 ? '#10B981' : manager.score >= 50 ? '#F59E0B' : '#EF4444',
                    }} />
                  </div>
                  <span style={insightsStyles.managerScoreValue}>{manager.score}%</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const insightsStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  recommendationsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  recommendationsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  recommendationsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  recommendationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  recommendationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
  },
  recommendationText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.5,
  },
  stressCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '4px',
  },
  chartSubtitle: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '20px',
  },
  stressMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  stressMetric: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  stressMetricIcon: {
    color: '#69277F',
  },
  stressMetricContent: {
    flex: 1,
  },
  stressMetricLabel: {
    fontSize: '13px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  stressMetricValue: {
    fontSize: '20px',
    fontWeight: 700,
  },
  middleRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  sentimentCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  sentimentChart: {
    marginTop: '20px',
  },
  sentimentLegend: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
  },
  sentimentLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#6B7280',
  },
  sentimentLegendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '4px',
  },
  workloadCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  workloadMetrics: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  workloadMetric: {
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
  },
  workloadMetricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  workloadMetricLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  workloadMetricValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#69277F',
    marginBottom: '4px',
  },
  workloadMetricDesc: {
    fontSize: '12px',
    color: '#6B7280',
  },
  managerEngagementCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  managerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  managerItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
  },
  managerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  managerAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '16px',
  },
  managerName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '2px',
  },
  managerStatus: {
    fontSize: '12px',
    color: '#6B7280',
  },
  managerScore: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '120px',
  },
  managerScoreBar: {
    flex: 1,
    height: '8px',
    background: '#E5E7EB',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  managerScoreFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  managerScoreValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    minWidth: '40px',
    textAlign: 'right',
  },
};

// Live Activity Tab Component
const LiveActivityTab: React.FC = () => {
  const [activities] = useState([
    { message: 'Dynami checked in with Hannah – mood neutral', time: '2m ago', type: 'check-in' },
    { message: 'Dynami flagged negative sentiment in #marketing-team', time: '5m ago', type: 'flag' },
    { message: 'Dynami suggested a 1:1 for Alex to manager Chris', time: '8m ago', type: 'suggestion' },
    { message: 'Dynami detected workload spike for Engineering team', time: '12m ago', type: 'detection' },
    { message: 'Dynami completed sentiment scan of #engineering channel', time: '15m ago', type: 'scan' },
    { message: 'Dynami scheduled follow-up check-in for Sarah Martinez', time: '18m ago', type: 'schedule' },
    { message: 'Dynami updated DDI score for Michael Rodriguez: 72 → 68', time: '22m ago', type: 'update' },
    { message: 'Dynami sent manager alert to David Kim about team workload', time: '25m ago', type: 'alert' },
  ]);

  return (
    <div style={liveActivityStyles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={liveActivityStyles.header}>
          <div>
            <h2 style={liveActivityStyles.title}>Live Activity</h2>
            <p style={liveActivityStyles.subtitle}>
              Real-time feed of Dynami actions across your organization
            </p>
          </div>
        </div>

        <motion.div
          style={liveActivityStyles.card}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={liveActivityStyles.cardHeader}>
            <MessageSquare size={20} color="#06BCCA" />
            <h3 style={liveActivityStyles.cardTitle}>Live Activity Preview</h3>
            <div style={liveActivityStyles.statusIndicator}>
              <div style={liveActivityStyles.statusDot} />
              <span>Live</span>
            </div>
          </div>
          <div style={liveActivityStyles.activityList}>
            {activities.map((item, index) => (
              <motion.div
                key={index}
                style={liveActivityStyles.activityItem}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div style={liveActivityStyles.activityDot} />
                <div style={liveActivityStyles.activityContent}>
                  <div style={liveActivityStyles.activityMessage}>{item.message}</div>
                  <div style={liveActivityStyles.activityTime}>{item.time}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const liveActivityStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    justifyContent: 'space-between',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
    flex: 1,
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: '#D1FAE5',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#059669',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#10B981',
    animation: 'pulse 2s infinite',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '10px',
    transition: 'background 0.2s ease',
  },
  activityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#06BCCA',
    marginTop: '6px',
    flexShrink: 0,
  },
  activityContent: {
    flex: 1,
  },
  activityMessage: {
    fontSize: '14px',
    color: '#374151',
    marginBottom: '4px',
  },
  activityTime: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
};

// Red Flags Tab Component
interface RedFlagsTabProps {
  employees: Employee[];
}

const RedFlagsTab: React.FC<RedFlagsTabProps> = ({ employees }) => {
  const navigate = useNavigate();
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  const redFlagEmployees = employees
    .filter(emp => emp.riskLevel === 'High')
    .sort((a, b) => b.riskScore - a.riskScore);

  interface Intervention {
    id: string;
    type: 'check-in' | 'manager-alert' | 'meeting-scheduled' | 'ddi-update' | 'action-plan';
    title: string;
    description: string;
    timestamp: string;
    status: 'completed' | 'in-progress' | 'scheduled';
    icon: React.ReactNode;
    color: string;
  }

  const getInterventions = (employee: Employee): Intervention[] => {
    const interventions: Intervention[] = [];
    const daysAgo = Math.floor(Math.random() * 7);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    // Always add check-in
    interventions.push({
      id: `${employee.id}-checkin`,
      type: 'check-in',
      title: 'Proactive Check-in Initiated',
      description: `Dynami initiated a Slack conversation to assess engagement levels`,
      timestamp: `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 10:32 AM`,
      status: 'completed',
      icon: <MessageSquare size={18} />,
      color: '#06BCCA',
    });

    // Add manager alert if high risk
    if (employee.riskScore >= 70) {
      interventions.push({
        id: `${employee.id}-alert`,
        type: 'manager-alert',
        title: 'Manager Alert Sent',
        description: `Alerted ${employee.manager} with intervention recommendations and talking points`,
        timestamp: `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 10:38 AM`,
        status: 'completed',
        icon: <Bell size={18} />,
        color: '#FF68C5',
      });
    }

    // Add meeting if very high risk
    if (employee.riskScore >= 75) {
      const meetingDate = new Date(date);
      meetingDate.setDate(meetingDate.getDate() + 1);
      interventions.push({
        id: `${employee.id}-meeting`,
        type: 'meeting-scheduled',
        title: '1:1 Meeting Scheduled',
        description: `Automatically scheduled 1:1 between ${employee.name} and ${employee.manager} for ${meetingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 2:00 PM`,
        timestamp: `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 10:40 AM`,
        status: 'scheduled',
        icon: <Calendar size={18} />,
        color: '#10B981',
      });
    }

    // Add DDI update if intervention is in progress
    if (employee.riskScore >= 70) {
      const updateDate = new Date(date);
      updateDate.setDate(updateDate.getDate() + 1);
      interventions.push({
        id: `${employee.id}-ddi`,
        type: 'ddi-update',
        title: 'DDI Score Updated',
        description: `DDI adjusted from ${employee.riskScore} to ${Math.max(employee.riskScore - 6, 30)} based on intervention progress`,
        timestamp: `${updateDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 11:15 AM`,
        status: 'completed',
        icon: <BarChart3 size={18} />,
        color: '#69277F',
      });
    }

    // Add action plan for highest risk
    if (employee.riskScore >= 80) {
      interventions.push({
        id: `${employee.id}-plan`,
        type: 'action-plan',
        title: 'AI Action Plan Generated',
        description: `Personalized intervention playbook created with specific recommendations for ${employee.manager}`,
        timestamp: `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 10:45 AM`,
        status: 'completed',
        icon: <Brain size={18} />,
        color: '#FF9C0D',
      });
    }

    return interventions;
  };

  const getRiskSeverity = (score: number) => {
    if (score >= 80) return { level: 'Critical', color: '#DC2626', bg: '#FEE2E2' };
    if (score >= 70) return { level: 'High', color: '#EF4444', bg: '#FEE2E2' };
    return { level: 'Elevated', color: '#F59E0B', bg: '#FEF3C7' };
  };

  return (
    <div style={redFlagsStyles.container}>
      {/* Header Stats */}
      <motion.div
        style={redFlagsStyles.header}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <div style={redFlagsStyles.headerTop}>
            <AlertTriangle size={32} color="#EF4444" />
            <h2 style={redFlagsStyles.title}>Red Flag Employees</h2>
          </div>
          <p style={redFlagsStyles.subtitle}>
            High-risk employees requiring immediate attention and automated interventions
          </p>
        </div>
        <div style={redFlagsStyles.statsRow}>
          <div style={redFlagsStyles.statBadge}>
            <span style={redFlagsStyles.statValue}>{redFlagEmployees.length}</span>
            <span style={redFlagsStyles.statLabel}>Red Flags</span>
          </div>
          <div style={redFlagsStyles.statBadge}>
            <span style={redFlagsStyles.statValue}>
              {redFlagEmployees.reduce((sum, emp) => sum + getInterventions(emp).length, 0)}
            </span>
            <span style={redFlagsStyles.statLabel}>Interventions</span>
          </div>
        </div>
      </motion.div>

      {/* Red Flag Employees List */}
      <div style={redFlagsStyles.employeesList}>
        {redFlagEmployees.map((employee, index) => {
          const interventions = getInterventions(employee);
          const severity = getRiskSeverity(employee.riskScore);
          const isExpanded = selectedEmployee === employee.id;

          return (
            <motion.div
              key={employee.id}
              style={redFlagsStyles.employeeCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              {/* Employee Header */}
              <div style={redFlagsStyles.employeeHeader}>
                <div style={redFlagsStyles.employeeInfo}>
                  <div 
                    style={{ ...redFlagsStyles.employeeAvatar, cursor: 'pointer' }}
                    onClick={() => navigate(`/employee/${employee.id}`)}
                  >
                    {employee.name.charAt(0)}
                  </div>
                  <div style={redFlagsStyles.employeeDetails}>
                    <div style={redFlagsStyles.employeeNameRow}>
                      <h3 
                        style={{ ...redFlagsStyles.employeeName, cursor: 'pointer' }}
                        onClick={() => navigate(`/employee/${employee.id}`)}
                      >
                        {employee.name}
                      </h3>
                      <span
                        style={{
                          ...redFlagsStyles.severityBadge,
                          background: severity.bg,
                          color: severity.color,
                        }}
                      >
                        {severity.level} Risk
                      </span>
                    </div>
                    <div style={redFlagsStyles.employeeMeta}>
                      <span style={redFlagsStyles.metaItem}>
                        {employee.role} • {employee.department}
                      </span>
                      <span style={redFlagsStyles.metaItem}>
                        Manager: {employee.manager}
                      </span>
                    </div>
                  </div>
                </div>
                <div 
                  style={{ ...redFlagsStyles.employeeMetrics, cursor: 'pointer' }}
                  onClick={() => setSelectedEmployee(isExpanded ? null : employee.id)}
                >
                  <div style={redFlagsStyles.ddiDisplay}>
                    <div style={redFlagsStyles.ddiValue}>{employee.riskScore}</div>
                    <div style={redFlagsStyles.ddiLabel}>DDI</div>
                  </div>
                  <div style={redFlagsStyles.interventionCount}>
                    <Zap size={16} color="#69277F" />
                    <span>{interventions.length} Interventions</span>
                  </div>
                </div>
              </div>

              {/* Behavioral Flags */}
              {employee.behavioralFlags.length > 0 && (
                <div style={redFlagsStyles.flagsSection}>
                  <div style={redFlagsStyles.flagsLabel}>Red Flags Detected:</div>
                  <div style={redFlagsStyles.flagsList}>
                    {employee.behavioralFlags.map((flag, flagIndex) => (
                      <div key={flagIndex} style={redFlagsStyles.flagItem}>
                        <AlertTriangle size={14} color="#EF4444" />
                        <span>{flag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interventions Timeline */}
              {isExpanded && (
                <motion.div
                  style={redFlagsStyles.interventionsSection}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div style={redFlagsStyles.interventionsHeader}>
                    <Sparkles size={20} color="#69277F" />
                    <h4 style={redFlagsStyles.interventionsTitle}>Automated Interventions</h4>
                  </div>
                  <div style={redFlagsStyles.interventionsTimeline}>
                    {interventions.map((intervention, intIndex) => (
                      <motion.div
                        key={intervention.id}
                        style={redFlagsStyles.interventionItem}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: intIndex * 0.1 }}
                      >
                        <div
                          style={{
                            ...redFlagsStyles.interventionIcon,
                            background: `${intervention.color}15`,
                            color: intervention.color,
                          }}
                        >
                          {intervention.icon}
                        </div>
                        <div style={redFlagsStyles.interventionContent}>
                          <div style={redFlagsStyles.interventionHeaderRow}>
                            <h5 style={redFlagsStyles.interventionTitle}>{intervention.title}</h5>
                            <span
                              style={{
                                ...redFlagsStyles.interventionStatus,
                                background:
                                  intervention.status === 'completed'
                                    ? '#D1FAE5'
                                    : intervention.status === 'in-progress'
                                    ? '#FEF3C7'
                                    : '#DBEAFE',
                                color:
                                  intervention.status === 'completed'
                                    ? '#059669'
                                    : intervention.status === 'in-progress'
                                    ? '#D97706'
                                    : '#2563EB',
                              }}
                            >
                              {intervention.status === 'completed'
                                ? 'Completed'
                                : intervention.status === 'in-progress'
                                ? 'In Progress'
                                : 'Scheduled'}
                            </span>
                          </div>
                          <p style={redFlagsStyles.interventionDescription}>
                            {intervention.description}
                          </p>
                          <div style={redFlagsStyles.interventionTimestamp}>
                            <Clock size={12} />
                            <span>{intervention.timestamp}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Expand/Collapse Indicator */}
              <div
                style={redFlagsStyles.expandButton}
                onClick={() => setSelectedEmployee(isExpanded ? null : employee.id)}
              >
                {isExpanded ? 'Show Less' : `View ${interventions.length} Interventions`}
                <ChevronRight
                  size={16}
                  style={{
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {redFlagEmployees.length === 0 && (
        <motion.div
          style={redFlagsStyles.emptyState}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <CheckCircle2 size={48} color="#10B981" />
          <h3 style={redFlagsStyles.emptyTitle}>No Red Flags</h3>
          <p style={redFlagsStyles.emptyText}>
            Great news! All employees are currently at manageable risk levels.
          </p>
        </motion.div>
      )}
    </div>
  );
};

const redFlagsStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '15px',
    color: '#6B7280',
    margin: 0,
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
  },
  statBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 24px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    minWidth: '120px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#EF4444',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: 500,
  },
  employeesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  employeeCard: {
    background: 'white',
    borderRadius: '16px',
    border: '2px solid #FEE2E2',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  employeeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    transition: 'background 0.2s ease',
  },
  employeeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  employeeAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '20px',
  },
  employeeDetails: {
    flex: 1,
  },
  employeeNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px',
    flexWrap: 'wrap',
  },
  employeeName: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  severityBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  employeeMeta: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    fontSize: '14px',
    color: '#6B7280',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
  },
  employeeMetrics: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  ddiDisplay: {
    textAlign: 'center',
  },
  ddiValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#EF4444',
    lineHeight: 1,
  },
  ddiLabel: {
    fontSize: '11px',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  interventionCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#F3F4F6',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#69277F',
  },
  flagsSection: {
    padding: '16px 24px',
    background: '#FEF2F2',
    borderTop: '1px solid #FEE2E2',
    borderBottom: '1px solid #FEE2E2',
  },
  flagsLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#DC2626',
    marginBottom: '10px',
  },
  flagsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#991B1B',
  },
  interventionsSection: {
    padding: '24px',
    background: '#F9FAFB',
    borderTop: '1px solid #E5E7EB',
  },
  interventionsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  interventionsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  interventionsTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  interventionItem: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  interventionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  interventionContent: {
    flex: 1,
  },
  interventionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  interventionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  interventionStatus: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  interventionDescription: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.6,
    margin: '0 0 8px 0',
  },
  interventionTimestamp: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#9CA3AF',
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px',
    background: '#F9FAFB',
    borderTop: '1px solid #E5E7EB',
    fontSize: '14px',
    fontWeight: 600,
    color: '#69277F',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '16px 0 8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
};

// Slack Demo Tab Component
const SlackDemoTab: React.FC = () => {
  const [employeeMessages, setEmployeeMessages] = useState<SlackMessage[]>([]);
  const [managerMessages, setManagerMessages] = useState<SlackMessage[]>([]);
  const [agenticActions, setAgenticActions] = useState<AgenticAction[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  interface SlackMessage {
    id: number;
    type: 'bot' | 'user' | 'system';
    content: string;
    time: string;
    sender?: string;
  }

  interface AgenticAction {
    id: number;
    type: 'schedule' | 'update' | 'notify' | 'analyze';
    title: string;
    description: string;
    time: string;
    icon: React.ReactNode;
    color: string;
  }

  const employeeConversation: SlackMessage[] = [
    {
      id: 1,
      type: 'bot',
      content: "Hey Alex! 👋 Hope you're having a good day. Got a minute for a quick check-in?",
      time: '10:32 AM',
      sender: 'Dynami',
    },
    {
      id: 2,
      type: 'user',
      content: "Hey, yeah sure. What's up?",
      time: '10:34 AM',
      sender: 'Alex Johnson',
    },
    {
      id: 3,
      type: 'bot',
      content: "Just wanted to see how things are going! On a scale of 1-5, how would you rate your energy levels at work this week?",
      time: '10:34 AM',
      sender: 'Dynami',
    },
    {
      id: 4,
      type: 'user',
      content: '😕 2 - Been feeling pretty drained lately',
      time: '10:35 AM',
      sender: 'Alex Johnson',
    },
    {
      id: 5,
      type: 'bot',
      content: "Thanks for being honest with me. I've noticed you've been putting in some long hours lately. Is there anything specific that's been weighing on you?",
      time: '10:35 AM',
      sender: 'Dynami',
    },
    {
      id: 6,
      type: 'user',
      content: 'Yeah, the workload has been pretty overwhelming. I feel like I can never catch up.',
      time: '10:36 AM',
      sender: 'Alex Johnson',
    },
    {
      id: 7,
      type: 'bot',
      content: "Got it. Heavy workloads can feel overwhelming. Would you be open to having a conversation with Sarah (your manager) about prioritizing some of your current tasks?",
      time: '10:36 AM',
      sender: 'Dynami',
    },
    {
      id: 8,
      type: 'user',
      content: 'Yes, that would be helpful. Thanks!',
      time: '10:37 AM',
      sender: 'Alex Johnson',
    },
    {
      id: 9,
      type: 'bot',
      content: "Perfect! I'll let Sarah know that a workload check-in might be helpful. I'll keep your specific responses private. 🤝",
      time: '10:37 AM',
      sender: 'Dynami',
    },
  ];

  const managerNotifications: SlackMessage[] = [
    {
      id: 100,
      type: 'bot',
      content: "Hi Sarah! 👋 Quick heads up from Dynami: Alex mentioned they're feeling overwhelmed with workload. No cause for alarm — they're open to a conversation about priorities.",
      time: '10:38 AM',
      sender: 'Dynami',
    },
    {
      id: 101,
      type: 'system',
      content: '📅 1:1 scheduled with Alex for Thursday 2:00 PM',
      time: '10:38 AM',
    },
    {
      id: 102,
      type: 'bot',
      content: "Great! I've scheduled the 1:1 and Alex has been notified. Here are some talking points to consider:\n\n• Ask about specific blockers\n• Discuss deadline flexibility\n• Explore task delegation options\n• Check on work-life balance",
      time: '10:38 AM',
      sender: 'Dynami',
    },
  ];

  const actions: AgenticAction[] = [
    {
      id: 1,
      type: 'analyze',
      title: 'Analyzing Engagement Patterns',
      description: 'Detected declining energy levels and workload stress indicators',
      time: '10:35 AM',
      icon: <Brain size={18} />,
      color: '#69277F',
    },
    {
      id: 2,
      type: 'update',
      title: 'Updating DDI Score',
      description: 'Alex\'s DDI: 78 → 72 (intervention in progress)',
      time: '10:36 AM',
      icon: <BarChart3 size={18} />,
      color: '#06BCCA',
    },
    {
      id: 3,
      type: 'notify',
      title: 'Notifying Manager',
      description: 'Alert sent to Sarah Chen with intervention recommendations',
      time: '10:37 AM',
      icon: <Bell size={18} />,
      color: '#FF68C5',
    },
    {
      id: 4,
      type: 'schedule',
      title: 'Scheduling 1:1 Meeting',
      description: 'Calendar invite sent to Alex and Sarah for Thursday 2:00 PM',
      time: '10:38 AM',
      icon: <Calendar size={18} />,
      color: '#10B981',
    },
    {
      id: 5,
      type: 'update',
      title: 'Dashboard Sync',
      description: 'Updating engagement insights with new intervention status',
      time: '10:38 AM',
      icon: <Zap size={18} />,
      color: '#FF9C0D',
    },
  ];

  const startDemo = async () => {
    setIsRunning(true);
    setEmployeeMessages([]);
    setManagerMessages([]);
    setAgenticActions([]);
    setCurrentStep(0);

    // Employee conversation
    for (let i = 0; i < employeeConversation.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1200));
      setEmployeeMessages(prev => [...prev, employeeConversation[i]]);
      setCurrentStep(i + 1);

      // Trigger agentic actions at specific points
      if (i === 3) {
        // After employee says energy level is 2
        await new Promise(resolve => setTimeout(resolve, 800));
        setAgenticActions(prev => [...prev, actions[0]]);
      }
      if (i === 5) {
        // After employee mentions workload
        await new Promise(resolve => setTimeout(resolve, 800));
        setAgenticActions(prev => [...prev, actions[1]]);
      }
      if (i === 7) {
        // After employee agrees to talk with manager
        await new Promise(resolve => setTimeout(resolve, 800));
        setAgenticActions(prev => [...prev, actions[2]]);
      }
    }

    // Manager notifications
    await new Promise(resolve => setTimeout(resolve, 1000));
    for (let i = 0; i < managerNotifications.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setManagerMessages(prev => [...prev, managerNotifications[i]]);
      
      if (i === 1) {
        // After scheduling message
        await new Promise(resolve => setTimeout(resolve, 500));
        setAgenticActions(prev => [...prev, actions[3]]);
      }
      if (i === 2) {
        // After final manager message
        await new Promise(resolve => setTimeout(resolve, 500));
        setAgenticActions(prev => [...prev, actions[4]]);
      }
    }

    setIsRunning(false);
  };

  const resetDemo = () => {
    setEmployeeMessages([]);
    setManagerMessages([]);
    setAgenticActions([]);
    setCurrentStep(0);
    setIsRunning(false);
  };

  return (
    <div style={slackDemoStyles.container}>
      {/* Header */}
      <div style={slackDemoStyles.header}>
        <div>
          <h2 style={slackDemoStyles.title}>Slack Integration Demo</h2>
          <p style={slackDemoStyles.subtitle}>
            See how Dynami proactively engages with employees and managers
          </p>
        </div>
        <div style={slackDemoStyles.headerActions}>
          <button
            style={slackDemoStyles.button}
            onClick={isRunning ? resetDemo : startDemo}
            disabled={isRunning}
          >
            {isRunning ? 'Stop Demo' : 'Start Demo'}
          </button>
          {!isRunning && (employeeMessages.length > 0 || managerMessages.length > 0) && (
            <button
              style={{ ...slackDemoStyles.button, ...slackDemoStyles.buttonSecondary }}
              onClick={resetDemo}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={slackDemoStyles.grid}>
        {/* Employee Chat */}
        <motion.div
          style={slackDemoStyles.chatPanel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div style={slackDemoStyles.chatHeader}>
            <div style={slackDemoStyles.chatHeaderLeft}>
              <div style={slackDemoStyles.slackIcon}>
                <MessageSquare size={20} color="white" />
              </div>
              <div>
                <h3 style={slackDemoStyles.chatTitle}>#dynami-checkins</h3>
                <span style={slackDemoStyles.chatSubtitle}>Employee View — Alex Johnson</span>
              </div>
            </div>
          </div>

          <div style={slackDemoStyles.chatMessages}>
            <AnimatePresence>
              {employeeMessages.map((msg) => (
                <motion.div
                  key={msg.id}
                  style={{
                    ...slackDemoStyles.message,
                    ...(msg.type === 'user' ? slackDemoStyles.messageUser : {}),
                    ...(msg.type === 'system' ? slackDemoStyles.messageSystem : {}),
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {msg.type !== 'system' && (
                    <div style={slackDemoStyles.messageAvatar}>
                      {msg.type === 'bot' ? (
                        <Bot size={18} color="#06BCCA" />
                      ) : (
                        <User size={18} color="#69277F" />
                      )}
                    </div>
                  )}
                  <div style={slackDemoStyles.messageContent}>
                    {msg.type !== 'system' && (
                      <div style={slackDemoStyles.messageMeta}>
                        <span style={slackDemoStyles.messageSender}>{msg.sender}</span>
                        <span style={slackDemoStyles.messageTime}>{msg.time}</span>
                      </div>
                    )}
                    <p style={slackDemoStyles.messageText}>{msg.content}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Manager Chat */}
        <motion.div
          style={slackDemoStyles.chatPanel}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div style={slackDemoStyles.chatHeader}>
            <div style={slackDemoStyles.chatHeaderLeft}>
              <div style={{ ...slackDemoStyles.slackIcon, background: '#FF68C5' }}>
                <Bell size={20} color="white" />
              </div>
              <div>
                <h3 style={slackDemoStyles.chatTitle}>Manager Alert</h3>
                <span style={slackDemoStyles.chatSubtitle}>Sarah Chen — Engineering Manager</span>
              </div>
            </div>
          </div>

          <div style={slackDemoStyles.chatMessages}>
            <AnimatePresence>
              {managerMessages.map((msg) => (
                <motion.div
                  key={msg.id}
                  style={{
                    ...slackDemoStyles.message,
                    ...(msg.type === 'user' ? slackDemoStyles.messageUser : {}),
                    ...(msg.type === 'system' ? slackDemoStyles.messageSystem : {}),
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {msg.type !== 'system' && (
                    <div style={slackDemoStyles.messageAvatar}>
                      {msg.type === 'bot' ? (
                        <Bot size={18} color="#FF68C5" />
                      ) : (
                        <User size={18} color="#69277F" />
                      )}
                    </div>
                  )}
                  <div style={slackDemoStyles.messageContent}>
                    {msg.type !== 'system' && (
                      <div style={slackDemoStyles.messageMeta}>
                        <span style={slackDemoStyles.messageSender}>{msg.sender}</span>
                        <span style={slackDemoStyles.messageTime}>{msg.time}</span>
                      </div>
                    )}
                    <p style={slackDemoStyles.messageText}>{msg.content}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Agentic Actions Panel */}
      <motion.div
        style={slackDemoStyles.actionsPanel}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={slackDemoStyles.actionsHeader}>
          <div style={slackDemoStyles.actionsHeaderLeft}>
            <Sparkles size={20} color="#69277F" />
            <h3 style={slackDemoStyles.actionsTitle}>Agentic Interventions</h3>
          </div>
          <span style={slackDemoStyles.actionsCount}>
            {agenticActions.length} actions taken
          </span>
        </div>

        <div style={slackDemoStyles.actionsList}>
          <AnimatePresence>
            {agenticActions.map((action) => (
              <motion.div
                key={action.id}
                style={slackDemoStyles.actionItem}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div
                  style={{
                    ...slackDemoStyles.actionIcon,
                    background: `${action.color}15`,
                    color: action.color,
                  }}
                >
                  {action.icon}
                </div>
                <div style={slackDemoStyles.actionContent}>
                  <div style={slackDemoStyles.actionHeader}>
                    <h4 style={slackDemoStyles.actionTitle}>{action.title}</h4>
                    <span style={slackDemoStyles.actionTime}>{action.time}</span>
                  </div>
                  <p style={slackDemoStyles.actionDescription}>{action.description}</p>
                </div>
                <CheckCircle2 size={18} color="#10B981" />
              </motion.div>
            ))}
          </AnimatePresence>

          {agenticActions.length === 0 && (
            <div style={slackDemoStyles.emptyActions}>
              <Zap size={32} color="#D1D5DB" />
              <p style={slackDemoStyles.emptyText}>
                Agentic actions will appear here as Dynami takes interventions
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const slackDemoStyles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  button: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buttonSecondary: {
    background: '#F3F4F6',
    color: '#4B5563',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
  },
  chatPanel: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '500px',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #E5E7EB',
    background: '#FAFAFA',
  },
  chatHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  slackIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: '#06BCCA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  chatSubtitle: {
    fontSize: '12px',
    color: '#6B7280',
  },
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  message: {
    display: 'flex',
    gap: '12px',
  },
  messageUser: {
    flexDirection: 'row-reverse',
  },
  messageSystem: {
    justifyContent: 'center',
  },
  messageAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  messageContent: {
    flex: 1,
  },
  messageMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  messageSender: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  messageTime: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  messageText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.5,
    margin: 0,
    whiteSpace: 'pre-line',
  },
  actionsPanel: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #E5E7EB',
  },
  actionsHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  actionsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  actionsCount: {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  actionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actionContent: {
    flex: 1,
  },
  actionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  actionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  actionTime: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  actionDescription: {
    fontSize: '13px',
    color: '#6B7280',
    margin: 0,
    lineHeight: 1.5,
  },
  emptyActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '14px',
    color: '#9CA3AF',
    marginTop: '12px',
  },
};

export default Dashboard;

